import { useContext, useEffect } from 'react';
import * as HelpCenter from '@doist/help-center-utils';
import Footer from 'components/Footer/Footer';
import Header from 'components/help/Header/Header';
import BlogSection from 'components/help/home/blog-section/blog-section';
import CategoriesSection from 'components/help/home/categories-section/categories-section';
import { ContactUsSection } from 'components/help/home/contact-us-section/contact-us-section';
import IntroSection from 'components/help/home/intro-section/intro-section';
import RelevantArticlesSection from 'components/help/home/relevant-articles-section/relevant-articles-section';
import { ReviewsGrid } from 'components/help/home/reviews-grid/reviews-grid';
import Seo from 'components/Seo/Seo';
import { helpCenterAllowedCategories } from 'config/constants';
import { getAllLanguages } from 'i18n/constants';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import BannersClient from 'services/contentful/clients/BannersClient';
import { getContent } from 'services/help-center/content';
import { getOgImageURL } from 'services/ogimage';
import type { GetStaticPropsContext, Transifex } from 'types';
import type { PageBanner } from 'types/contentful';
import { UserContextV2 } from '@/app/services/providers/contexts/user-context';
type Props = {
  pageBanner: PageBanner;
  categories: HelpCenter.PartialCategory[];
};
export default function HelpHomePage({
  pageBanner,
  categories
}: Props) {
  const {
    t,
    i18n
  } = useTranslation('helpcenter');
  const {
    user,
    isAdmin
  } = useContext(UserContextV2);
  const utm_campaign = 'helpcenter';
  const ogimage = getOgImageURL('og-image-help-article.png', i18n.language);
  useEffect(() => {
    /** Auto-focus on the search input */
    if (window.innerWidth > 480) {
      const searchField = document.getElementById('article-search');
      if (searchField) {
        searchField.focus();
      }
    }
  }, []);
  return <>
            <Seo path="/help" title={t('seo.title')} description={t('seo.description')} images={[{
      url: ogimage,
      width: 1200,
      height: 628
    }]} data-sentry-element="Seo" data-sentry-source-file="index.tsx" />
            <Header data={{
      categories,
      user
    }} isAdmin={isAdmin} pageBanner={pageBanner} data-sentry-element="Header" data-sentry-source-file="index.tsx" />
            <main>
                <IntroSection data-sentry-element="IntroSection" data-sentry-source-file="index.tsx" />
                <CategoriesSection categories={categories} data-sentry-element="CategoriesSection" data-sentry-source-file="index.tsx" />
                <RelevantArticlesSection data-sentry-element="RelevantArticlesSection" data-sentry-source-file="index.tsx" />
                <ContactUsSection data-sentry-element="ContactUsSection" data-sentry-source-file="index.tsx" />
                <ReviewsGrid data-sentry-element="ReviewsGrid" data-sentry-source-file="index.tsx" />
                <BlogSection utm_campaign={utm_campaign} data-sentry-element="BlogSection" data-sentry-source-file="index.tsx" />
                <Footer title={t('footerTitle')} utm_campaign={utm_campaign} variant="neutral" data-sentry-element="Footer" data-sentry-source-file="index.tsx" />
            </main>
        </>;
}
export async function getStaticProps({
  params
}: GetStaticPropsContext) {
  const locale = params?.locale as Transifex.Languages;
  const [content, {
    _nextI18Next
  }] = await Promise.all([getContent(locale), serverSideTranslations(locale, ['common', 'helpcenter', 'home-teams'])]);
  const categories = HelpCenter.getPartialCategories(content.categories).filter(c => {
    return helpCenterAllowedCategories.includes(c.id);
  });
  const pageBanner = await BannersClient.getPageBanner('/help', {
    lang: locale,
    cacheEnabled: false
  });
  return {
    props: {
      _nextI18Next,
      pageBanner,
      categories
    },
    revalidate: 3600
  };
}
export async function getStaticPaths() {
  if (process.env.NEXT_PUBLIC_APP_ENV !== 'production') return {
    paths: [],
    fallback: 'blocking'
  };
  return {
    paths: getAllLanguages().map(locale => ({
      params: {
        locale
      }
    })),
    fallback: false
  };
}